/**
 * Shared delivery-ETA calculation and messaging.
 *
 * ONE source of truth for "when will this arrive", used by both the PDP store
 * locator (blocks/product-store-locator.liquid) and the cart page
 * (sections/cart.liquid). The PDP asks about a single product; the cart asks
 * about every line and shows the slowest one. Neither may compute dates of its
 * own - if the two surfaces ever disagree for the same product, that is a bug.
 *
 * Two layers:
 *   1. Dates    - base date -> next dispatch day -> arrival. Unchanged from the
 *                 original PDP implementation, just lifted out of it.
 *   2. Messaging - turns an arrival date into customer copy using two
 *                 lead-time tiers: a day count under ~4 weeks, an early/late
 *                 month part beyond that. Originally three tiers, with a season
 *                 beyond ~9 weeks; the season band was removed on client
 *                 direction (13 Aug, #19498858) after "arriving in Spring" was
 *                 reported as a bug - it tells a customer almost nothing.
 *
 * Everything here is pure: same inputs, same output, no DOM, no fetch. The
 * caller owns the worker requests, the DOM and the console logging.
 *
 * Data sources behind the inputs (never Shopify inventory):
 *   stockAvailable - variant metafield esr.stock_available (Rex via Celigo)
 *   etaTs          - effective warehouse ETA, epoch SECONDS (future eta_manual
 *                    beats eta, past dates already discarded by the caller)
 *   isPreorder     - product tag marketing_tag:coming_soon
 *   transit        - GET /transit?postcode= -> { transitDays, dispatchDays }
 *   collection     - GET /collection?outlet= -> { leadTimeMin, leadTimeMax }
 */
window.ESDeliveryETA = (function () {
  var WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  var DAY_MS = 86400000;

  // The only boundary now: under ~4 weeks gives a day count, 4+ weeks gives a
  // month part. The season band beyond ~9 weeks was removed on client direction
  // (13 Aug, #19498858) - a customer told "arriving in Spring" has been given
  // almost nothing, and it was reported as a bug the first time it appeared.
  var TIER_NEAR_MAX_DAYS = 28;

  /** Start of day, so day-count maths is never skewed by the clock time. */
  function startOfDay(d) {
    var x = new Date(d.getTime());
    x.setHours(0, 0, 0, 0);
    return x;
  }

  /** Whole days from a to b (both normalised to midnight). */
  function daysBetween(a, b) {
    return Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / DAY_MS);
  }

  /**
   * `n` calendar days after `date`, by calendar arithmetic rather than by
   * adding milliseconds.
   *
   * Adding n * 86400000 is a day short or a day long across a daylight-saving
   * boundary, because those local days are 23 or 25 hours. NZ turns the clocks
   * on 27 September and AU on 4 October, both inside the windows this file
   * quotes, and a picking step of several days makes the error easier to hit
   * than it was when every hop was a single day. setDate() moves the calendar
   * day and lets the runtime settle the offset.
   */
  function addDays(date, n) {
    var d = new Date(date.getTime());
    d.setDate(d.getDate() + n);
    return d;
  }

  /**
   * When the item becomes ready to leave the warehouse.
   *   pre-order with a usable ETA -> the ETA (the allocation ships when the
   *     incoming order lands, even while stock_available > 0)
   *   in stock                    -> today
   *   out of stock with an ETA    -> the ETA
   *   otherwise                   -> null (no honest date exists)
   * Returns { date, why, confident } - `why` feeds the QA console logs and
   * `confident` drives the green vs amber dot on the PDP.
   */
  function baseDate(opts) {
    var stock = (opts && opts.stockAvailable) || 0;
    var etaTs = (opts && opts.etaTs) || 0;
    if (opts && opts.isPreorder && etaTs > 0) {
      return { date: new Date(etaTs * 1000), why: 'pre-order -> warehouse ETA', confident: false };
    }
    if (stock > 0) {
      return { date: new Date(), why: 'in stock (esr.stock_available = ' + stock + ') -> today', confident: true };
    }
    if (etaTs > 0) {
      return { date: new Date(etaTs * 1000), why: 'out of stock -> warehouse ETA', confident: false };
    }
    return { date: null, why: 'no stock and no usable ETA', confident: false };
  }

  /** First allowed dispatch weekday on or after `from`. Null if none in a week. */
  function nextDispatchDate(from, dispatchDays) {
    var allowed = {};
    (dispatchDays || []).forEach(function (d) { allowed[String(d).toLowerCase()] = true; });
    var d = new Date(from.getTime());
    for (var i = 0; i < 7; i++) {
      if (allowed[WEEKDAYS[d.getDay()]]) { return d; }
      d = addDays(d, 1);
    }
    return null;
  }

  /**
   * Home-delivery arrival for one product, following the formula the client
   * confirmed on Productive #1123:
   *
   *   base date + picking time -> next available dispatch day -> + transit
   *
   * Picking and transit are both CALENDAR days, and the picking clock starts on
   * the base date itself, so a 3-day pick beginning Thursday is ready Sunday
   * (confirmed 27 Aug). Public holidays are not modelled anywhere in this
   * pipeline, so an estimate spanning one runs early.
   *
   * `pickingDays` is absent for a market whose postcode file predates the
   * dispatch-based rewrite (AU, as at 27 Aug) and from any worker older than
   * that change, and reads as 0 in both cases - which is exactly the old
   * behaviour, base date straight to the next dispatch day. That is what makes
   * a new theme safe against an old worker.
   *
   * Returns { arrival, arrivalMin, arrivalMax, dispatch, picked, pickingDays,
   * base, why, confident } or null when there is no base date or no dispatch
   * day to work from.
   */
  function homeArrival(opts, transit) {
    if (!transit || !transit.found) { return null; }
    var b = baseDate(opts);
    if (!b.date) { return null; }
    var picking = transit.pickingDays || 0;
    // When the order is picked and can go on the next truck out.
    var picked = addDays(b.date, picking);
    var dispatch = nextDispatchDate(picked, transit.dispatchDays);
    if (!dispatch) { return null; }
    /* Transit is a window (Aug 17 postcode files). `transitDays` is the worker's
       legacy single figure, kept for a client cached from before the window
       landed and equal to the minimum, so an older cached copy of this file
       still resolves to the same date it always did. */
    var min = transit.transitDaysMin != null ? transit.transitDaysMin : (transit.transitDays || 0);
    var max = transit.transitDaysMax != null ? transit.transitDaysMax : min;
    return {
      base: b.date,
      why: b.why,
      confident: b.confident,
      pickingDays: picking,
      picked: picked,
      dispatch: dispatch,
      arrivalMin: addDays(dispatch, min),
      arrivalMax: addDays(dispatch, max),
      /* The late end. Everything that reduces a window to one date - tiering,
         "the slowest of these components", the cart's group header - takes the
         end it cannot arrive after, never the optimistic one. */
      arrival: addDays(dispatch, max)
    };
  }

  /**
   * Store-collection window for one product: the base date plus the store's
   * lead time, as a min/max pair. Returns { min, max, base, why, confident }
   * or null when there is no base date.
   *
   * Picking time is deliberately NOT added here. Collection lead times come
   * from a different supply - the store collection sheet, not the postcode
   * file - and there is nothing to say whether they already include the pick.
   * Adding it would risk counting the same three days twice. Raised on #1123.
   */
  function collectionWindow(opts, collection) {
    if (!collection || !collection.found) { return null; }
    var b = baseDate(opts);
    if (!b.date) { return null; }
    return {
      base: b.date,
      why: b.why,
      confident: b.confident,
      min: addDays(b.date, collection.leadTimeMin || 0),
      max: addDays(b.date, collection.leadTimeMax || 0)
    };
  }

  /**
   * "Early" / "Mid" / "Late" for a date, by day of month. `lower` returns the
   * lower-case form the cart designs use mid-sentence ("late September").
   */
  function monthPart(d, lower) {
    var day = d.getDate();
    // Early or Late only - the client's wording is "early or late month"
    // (#19498858), so the month splits in two rather than three. "Mid" was
    // dropped with the same instruction.
    // The 15th itself is LATE: "from the 15th of the month onward is late,
    // before the 15th is early" (Daniel, 2 Sep, #1148). It used to sit on the
    // early side. One function, so the PDP and the cart move together.
    var part = day < 15 ? 'Early' : 'Late';
    return lower ? part.toLowerCase() : part;
  }

  function monthName(d) {
    return d.toLocaleDateString('en-AU', { month: 'long' });
  }

  /**
   * Which tier a date falls into, relative to `from` (default today):
   *   'near' under ~4 weeks | 'mid' everything beyond that
   *
   * 'far' no longer exists. Callers that still branch on it fall through to the
   * 'mid' wording, which is the intended behaviour rather than an oversight.
   */
  function tierFor(date, from) {
    var days = daysBetween(from || new Date(), date);
    if (days <= TIER_NEAR_MAX_DAYS) { return 'near'; }
    return 'mid';
  }

  /**
   * Heading for the cart summary's delivery estimate. Wording follows the cart
   * designs ("Estimated delivery late September 2026" - lower-case month part,
   * year shown, no full stop); the three bands come from the delivery messaging
   * terminology. Returns { tier, days, text }.
   *
   *   near -> "Arriving in 5 days to your location or store"
   *   mid  -> "Arriving in late September 2026"
   *
   * Wording supplied by the client (#19498858). The year is kept on the month
   * band: with the season band gone this now covers everything beyond four
   * weeks, including dates in the following year, and "late February" alone
   * would not say which one.
   */
  function deliveryMessage(date, from) {
    var base = from || new Date();
    var days = daysBetween(base, date);
    var tier = tierFor(date, base);
    if (tier === 'near') {
      return { tier: tier, days: days, text: 'Arriving in ' + Math.max(days, 1) + ' day' + (days === 1 ? '' : 's') + ' to your location or store' };
    }
    return { tier: tier, days: days, text: 'Arriving in ' + monthPart(date, true) + ' ' + monthName(date) + ' ' + date.getFullYear() };
  }

  /**
   * The cart's delivery estimate for a min/max window - the same sentence as
   * deliveryMessage, with the window's wording in place of a day count:
   *
   *   near -> "Arriving 21 - 27 August to your location or store"
   *   mid  -> "Arriving in October 2026"                    (both ends in one month)
   *        -> "Arriving in late September - early October 2026" (ends differ)
   *
   * A window whose ends fall in the SAME month reads as the bare month, on
   * client preference (27 Aug): "early October - late October" told a shopper
   * less than "October" did while taking three times the words. Ends in
   * different months keep both parts, which is where the parts earn their
   * place. Note this also collapses the case where both parts were already
   * equal - "early October - early October" was printed as "early October" and
   * now reads "October" too, so the rule is one sentence rather than two.
   *
   * The late end decides the tier, as everywhere else. The year stays on the
   * month band for the reason deliveryMessage gives: it now covers everything
   * beyond four weeks, including next year.
   */
  function deliveryWindowMessage(minD, maxD, from) {
    var base = from || new Date();
    var tier = tierFor(maxD, base);
    var days = daysBetween(base, maxD);
    if (tier === 'near') {
      return { tier: tier, days: days, text: 'Arriving ' + fmtRange(minD, maxD) + ' to your location or store' };
    }
    var sameMonth = monthName(minD) === monthName(maxD) && minD.getFullYear() === maxD.getFullYear();
    var span;
    if (sameMonth) {
      span = monthName(maxD);
    } else {
      span = monthPart(minD, true) + ' ' + monthName(minD) + ' - ' + monthPart(maxD, true) + ' ' + monthName(maxD);
    }
    return { tier: tier, days: days, text: 'Arriving in ' + span + ' ' + maxD.getFullYear() };
  }

  /**
   * The client's approved heading for the cart's delivery estimate, shown in
   * BOTH places the estimate appears - the block at the top of the cart and the
   * summary box - because the repetition is deliberate (Daniel, 1 Sep, #1148).
   * Returns { tier, days, text }.
   *
   *   "Estimated Delivery October 2026"
   *   "Estimated Delivery early September - late October 2026"  (spans months)
   *
   * Month granularity in EVERY tier, not only beyond four weeks. The client
   * approved the shape "Delivery <Date>" against a month-band example - no "in",
   * per his edit to 17063290 - so a near-tier cart reads "Delivery August 2026"
   * where the old heading
   * read "Arriving 21 - 27 August to your location or store". That is a loss of
   * precision on the most time-sensitive carts, so it is flagged for Alex
   * rather than settled here - deliveryWindowMessage above still holds the
   * day-range wording if he wants it back.
   *
   * Since 3 Sep (#1148 items 4 and 5) this heading is only reached WITHOUT a
   * saved location - the dispatch-month fallback - and its lead word is
   * "Estimated Delivery", the client's text. With a location the heading is
   * estimateHeadingMessage below, which carries the product page's own range.
   */
  function deliveryMonthMessage(minD, maxD, from) {
    var base = from || new Date();
    var sameMonth = monthName(minD) === monthName(maxD) && minD.getFullYear() === maxD.getFullYear();
    var span = sameMonth
      ? monthName(maxD)
      : monthPart(minD, true) + ' ' + monthName(minD) + ' - ' + monthPart(maxD, true) + ' ' + monthName(maxD);
    return {
      tier: tierFor(maxD, base),
      days: daysBetween(base, maxD),
      text: 'Estimated Delivery ' + span + ' ' + maxD.getFullYear()
    };
  }

  /**
   * The same two tiers worded for store collection, used when the customer
   * has chosen a store rather than given a postcode - promising "delivery" for
   * an order they intend to collect would be wrong.
   * Returns { tier, days, text }.
   *
   *   near -> "Ready for collection within 14 days"
   *   mid  -> "Ready for collection late September 2026"
   *
   * Wording left as-is for now. The client's single "to your location or store"
   * message would replace this one too, but whether the collection and dispatch
   * sentences collapse into it is unconfirmed (asked on #19498858), so only the
   * season band has gone.
   */
  function collectionMessage(date, from) {
    var base = from || new Date();
    var days = daysBetween(base, date);
    var tier = tierFor(date, base);
    if (tier === 'near') {
      return { tier: tier, days: days, text: 'Ready for collection within ' + Math.max(days, 1) + ' day' + (days === 1 ? '' : 's') };
    }
    return { tier: tier, days: days, text: 'Ready for collection ' + monthPart(date, true) + ' ' + monthName(date) + ' ' + date.getFullYear() };
  }

  /**
   * Dispatch-side sentence for the cart's group header, which talks about when
   * items ship rather than when they land. Same tiers, Figma's wording.
   * Returns { tier, days, text }.
   *
   *   near (and in stock)  -> "In stock and ready to ship"
   *   near (awaiting stock)-> "Shipping within 5 days"
   *   mid                  -> "Shipping from late September"
   *
   * Same note as collectionMessage: only the season band has gone.
   */
  function shippingMessage(date, from, readyNow) {
    var base = from || new Date();
    var days = daysBetween(base, date);
    var tier = tierFor(date, base);
    if (tier === 'near') {
      if (readyNow) { return { tier: tier, days: days, text: 'In stock and ready to ship' }; }
      return { tier: tier, days: days, text: 'Shipping within ' + Math.max(days, 1) + ' day' + (days === 1 ? '' : 's') };
    }
    return { tier: tier, days: days, text: 'Shipping from ' + monthPart(date, true) + ' ' + monthName(date) };
  }

  /**
   * Per-line dispatch date for a cart row - "Due 12 September 2026".
   *
   * The client chose the word themselves (Daniel, 1 Sep, #1148): "Due" replaces
   * "Shipping from" because it carries no promise about the DELIVERY date, only
   * about the stock. The date itself is unchanged - the wording moved, the
   * calculation did not.
   *
   * The cart groups into just two buckets (in stock / not in stock), so a
   * heading can no longer name a date the way "Shipping within 22 days" did.
   * This puts the date back, per line, which is what the client asked for:
   * the shopper can see which items are holding the order up (#19498858).
   *
   * An exact day, not a tier: within one bucket the tier wording would repeat
   * itself down the list and say nothing about the difference between rows.
   */
  function longDate(date) {
    return date.getDate() + ' ' + monthName(date) + ' ' + date.getFullYear();
  }

  function lineDueMessage(date) {
    return { text: 'Due ' + longDate(date) };
  }

  /**
   * The store-collection window as customer copy - the PDP's "Ready for pickup"
   * rule (fmtPickupWindow in blocks/product-store-locator.liquid), restated
   * here so the cart can quote a collection window in the PDP's own words:
   *
   *   max within four weeks -> "17 - 20 September"
   *   max beyond four weeks -> "Late September" (the LATER end's month part)
   *
   * Unlike windowMessage it never spans two month parts - that is how the PDP
   * pickup line reads, and the cart matches it rather than improving on it.
   */
  function pickupWindowMessage(minD, maxD, from) {
    var base = from || new Date();
    var tier = tierFor(maxD, base);
    var days = daysBetween(base, maxD);
    if (tier === 'near') {
      return { tier: tier, days: days, text: fmtRange(minD, maxD) };
    }
    return { tier: tier, days: days, text: monthPart(maxD) + ' ' + monthName(maxD) };
  }

  /**
   * The text for an ARRIVAL window in exactly the words the product page uses
   * for the same product and location (#1148, items 4 and 5): windowMessage for
   * home delivery (the PDP's "Standard delivery" row), pickupWindowMessage for
   * collection (its "Ready for pickup" row).
   */
  function arrivalWindowText(minD, maxD, mode, from) {
    var msg = mode === 'collection' ? pickupWindowMessage(minD, maxD, from) : windowMessage(minD, maxD, from);
    return msg.text;
  }

  /**
   * Per-line arrival for a cart row once a location is known - "Due 12 - 15
   * September", "Due Late October". "Due" is the client's word (1 Sep); the
   * range is the product page's (2 Sep: the cart's dates must match it). It
   * replaces the dispatch-date label (lineDueMessage) on the rows outside the
   * in-stock bucket once the transit or collection lookup has answered.
   */
  function lineArrivalMessage(minD, maxD, mode, from) {
    return { text: 'Due ' + arrivalWindowText(minD, maxD, mode, from) };
  }

  /**
   * The estimate heading for a cart WITH a saved location, painted into all
   * three places the cart states it (drawer band, top of cart, Order Summary):
   * "Estimated Delivery 12 - 15 September", "Estimated Delivery Late October".
   * The wording is the client's (2 Sep, #1148), the range is the product
   * page's for the SLOWEST line, so it agrees with that line's own "Due ..."
   * label and with the product page. No year: the PDP range carries none.
   */
  function estimateHeadingMessage(minD, maxD, mode, from) {
    return { text: 'Estimated Delivery ' + arrivalWindowText(minD, maxD, mode, from) };
  }

  /** Long date for the QA console logs. */
  function fmtCalcDay(d) {
    return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
  }

  /**
   * The customer-facing value for a min/max window, following the rules signed
   * off on #1072:
   *
   *   max within four weeks   -> "21 - 27 August" (dates, as the pickup line
   *                              already shows), collapsing to "21 August" when
   *                              both ends land on the same day
   *   max beyond four weeks   -> "Late October" when both ends share a month
   *                              part, or "Late September - Early October"
   *                              (both parts) when they differ
   *
   * The month part is ALWAYS shown beyond four weeks, capitalised ("Late
   * October", "Late September - Early October"). It was lower case from the
   * 2 Sep spec (#1148, "Due late October") until 8 Sep, when the client asked
   * for the capital everywhere after seeing the product page print "Standard
   * delivery: early October" beside "Ready for pickup: Early October" - the
   * pickup formatter never lower-cased. This is the shared formatter, so the
   * product page, the cart's "Due ..." rows and its "Estimated Delivery ..."
   * heading move together; the checkout extension's windowText mirrors it.
   * Same-month windows still collapse to one part, never "Late October - Late
   * October".
   *
   * The month test compares the DATES, not the formatted strings: this
   * formatter carries no year, so an October 2026 to October 2027 window would
   * otherwise read as one part and hide twelve months. Ends in different years
   * keep both parts even when the month name matches.
   *
   * The LATE end decides which applies, matching pickup: a window is never
   * presented as more precise than the end it cannot arrive after.
   *
   * Returns { tier, days, text } - `days` being days to the late end, so a
   * caller can log the working.
   */
  function windowMessage(minD, maxD, from) {
    var base = from || new Date();
    var tier = tierFor(maxD, base);
    var days = daysBetween(base, maxD);
    if (tier === 'near') {
      return { tier: tier, days: days, text: fmtRange(minD, maxD) };
    }
    var partMin = monthPart(minD) + ' ' + monthName(minD);
    var partMax = monthPart(maxD) + ' ' + monthName(maxD);
    if (partMin === partMax && minD.getFullYear() === maxD.getFullYear()) {
      return { tier: tier, days: days, text: partMax };
    }
    return { tier: tier, days: days, text: partMin + ' - ' + partMax };
  }

  function fmtRange(minD, maxD) {
    var monthMin = monthName(minD);
    var monthMax = monthName(maxD);
    if (monthMin === monthMax && minD.getFullYear() === maxD.getFullYear()) {
      // Equal ends (an older single-figure transit file) read as one date.
      if (minD.getDate() === maxD.getDate()) { return maxD.getDate() + ' ' + monthMax; }
      return minD.getDate() + ' - ' + maxD.getDate() + ' ' + monthMax;
    }
    return minD.getDate() + ' ' + monthMin + ' - ' + maxD.getDate() + ' ' + monthMax;
  }

  return {
    WEEKDAYS: WEEKDAYS,
    TIER_NEAR_MAX_DAYS: TIER_NEAR_MAX_DAYS,
    startOfDay: startOfDay,
    daysBetween: daysBetween,
    addDays: addDays,
    baseDate: baseDate,
    nextDispatchDate: nextDispatchDate,
    homeArrival: homeArrival,
    collectionWindow: collectionWindow,
    monthPart: monthPart,
    monthName: monthName,
    tierFor: tierFor,
    deliveryMessage: deliveryMessage,
    windowMessage: windowMessage,
    deliveryWindowMessage: deliveryWindowMessage,
    deliveryMonthMessage: deliveryMonthMessage,
    collectionMessage: collectionMessage,
    shippingMessage: shippingMessage,
    lineDueMessage: lineDueMessage,
    longDate: longDate,
    pickupWindowMessage: pickupWindowMessage,
    arrivalWindowText: arrivalWindowText,
    lineArrivalMessage: lineArrivalMessage,
    estimateHeadingMessage: estimateHeadingMessage,
    fmtCalcDay: fmtCalcDay,
    fmtRange: fmtRange
  };
})();
